/**
 * Road-snap utilities. Given a GeoJSON FeatureCollection of sample points +
 * a road network (LineStrings from /api/overpass), move each Point feature
 * to the nearest road segment with a small perpendicular jitter so multiple
 * crashes don't pile on the same pixel.
 *
 * Only Point geometries are snapped — polygons, lines, and the hotspot zones
 * stay as generated (they're already at sensible locations).
 *
 * Equirectangular meters: at the latitudes we care about (continental US),
 * the error vs true geodesic is <0.5% over a few miles. Good enough for
 * sample-data placement.
 */

const M_PER_DEG_LAT = 111_320;
function mPerDegLon(lat) { return 111_320 * Math.cos((lat * Math.PI) / 180); }

// Convert a (lon,lat) to local meters around an origin.
function toLocalM(lon, lat, originLat, originLon) {
  const k = mPerDegLon(originLat);
  return [(lon - originLon) * k, (lat - originLat) * M_PER_DEG_LAT];
}
function toLonLat(x, y, originLat, originLon) {
  const k = mPerDegLon(originLat);
  return [originLon + x / k, originLat + y / M_PER_DEG_LAT];
}

// Project point P onto segment AB; return {x, y, t, d2} where t in [0,1].
function projectOnSegment(px, py, ax, ay, bx, by) {
  const abx = bx - ax, aby = by - ay;
  const len2 = abx * abx + aby * aby;
  if (len2 === 0) {
    const dx = px - ax, dy = py - ay;
    return { x: ax, y: ay, t: 0, d2: dx * dx + dy * dy };
  }
  let t = ((px - ax) * abx + (py - ay) * aby) / len2;
  t = Math.max(0, Math.min(1, t));
  const x = ax + t * abx, y = ay + t * aby;
  const dx = px - x, dy = py - y;
  return { x, y, t, d2: dx * dx + dy * dy };
}

/**
 * Build an array of segments in local-meter coords for fast iteration.
 * Pre-filters segments to those whose midpoint is within `clipMiles` of the
 * data centroid — Overpass responses cover the bbox, this lets us skip
 * far-away roads when snapping a small cluster.
 */
function buildSegments(roads, originLat, originLon) {
  const segs = [];
  for (const f of roads.features || []) {
    if (f.geometry?.type !== 'LineString') continue;
    const coords = f.geometry.coordinates;
    for (let i = 1; i < coords.length; i++) {
      const [alon, alat] = coords[i - 1];
      const [blon, blat] = coords[i];
      const [ax, ay] = toLocalM(alon, alat, originLat, originLon);
      const [bx, by] = toLocalM(blon, blat, originLat, originLon);
      segs.push({ ax, ay, bx, by });
    }
  }
  return segs;
}

/**
 * Snap a single (lon, lat) point to the nearest segment. Returns the snapped
 * (lon, lat) with a small perpendicular jitter so co-located points spread
 * out a few meters either side of the road centerline.
 */
function snapOne(lon, lat, segs, originLat, originLon, jitterM, rng) {
  const [px, py] = toLocalM(lon, lat, originLat, originLon);
  let best = null;
  for (const s of segs) {
    const r = projectOnSegment(px, py, s.ax, s.ay, s.bx, s.by);
    if (!best || r.d2 < best.d2) best = { ...r, seg: s };
  }
  if (!best) return [lon, lat];
  // Perpendicular unit vector to the segment.
  const dx = best.seg.bx - best.seg.ax, dy = best.seg.by - best.seg.ay;
  const len = Math.hypot(dx, dy) || 1;
  const nx = -dy / len, ny = dx / len;
  const j = ((rng ? rng() : Math.random()) * 2 - 1) * jitterM;
  return toLonLat(best.x + nx * j, best.y + ny * j, originLat, originLon);
}

/**
 * Snap all Point features in `data` to the nearest road in `roads`. Returns
 * a new FeatureCollection (does not mutate input).
 *
 * `centerLat/Lon` is the demo origin — used both as projection origin and
 * to clip far-away road segments.
 */
/**
 * Snap distance thresholds in METERS. If the nearest road is farther than
 * these, the point is likely in water (or well off the road network) and
 * the feature is dropped from the map rather than shown floating.
 * Different categories tolerate different distances — a crash point should
 * be very close to a road; a shelter can be further from the centerline.
 */
const MAX_SNAP_M = {
  'crash-point': 200,
  'emergency-hospital': 600,
  'emergency-eoc': 600,
  'emergency-fire': 400,
  'emergency-ems': 400,
  'event-gate': 250,
  'event-medical': 250,
  'event-security': 250,
  'permit-point': 400,
  default: 400,
};

export function snapPointsToRoads(data, roads, centerLat, centerLon) {
  if (!roads || !roads.features || roads.features.length === 0) return data;
  const segs = buildSegments(roads, centerLat, centerLon);
  if (segs.length === 0) return data;

  // Cheap PRNG seeded by point index — keeps jitter deterministic per demo.
  let s = 0x9e3779b9;
  const rng = () => {
    s = (s + 0x6d2b79f5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };

  const features = data.features
    .map((f) => {
      if (f.geometry?.type !== 'Point') return f;
      const cat = f.properties?.atlasCategory || '';
      const [lon, lat] = f.geometry.coordinates;

      // Measure distance to nearest road first. If beyond category max, drop
      // the feature entirely — that's how we prevent hospitals-in-the-harbor
      // when city bbox generation still lands a point near water. Returning
      // null here is filtered out below.
      const [px, py] = toLocalM(lon, lat, centerLat, centerLon);
      let best = null;
      for (const seg of segs) {
        const r = projectOnSegment(px, py, seg.ax, seg.ay, seg.bx, seg.by);
        if (!best || r.d2 < best.d2) best = { ...r, seg };
      }
      if (!best) return null;
      const distM = Math.sqrt(best.d2);
      const maxM = MAX_SNAP_M[cat] || MAX_SNAP_M.default;
      if (distM > maxM) return null;

      // Larger jitter for landmark facilities so multiple hospitals don't
      // stack on the same intersection.
      const jitter = cat === 'crash-point' ? 6
        : (cat === 'emergency-hospital' || cat === 'emergency-eoc') ? 12
        : 3; // meters
      const dx = best.seg.bx - best.seg.ax, dy = best.seg.by - best.seg.ay;
      const len = Math.hypot(dx, dy) || 1;
      const nx = -dy / len, ny = dx / len;
      const j = (rng() * 2 - 1) * jitter;
      const snapped = toLonLat(best.x + nx * j, best.y + ny * j, centerLat, centerLon);
      return { ...f, geometry: { type: 'Point', coordinates: snapped } };
    })
    .filter(Boolean);

  return { ...data, features };
}
